<script setup lang="ts">
import type { DungeonRunWithPlace } from '~/composables/charon'

const { t } = useI18n()

const props = defineProps<{
  runs: DungeonRunWithPlace[] | null
  page: number
  maxPage: number
  size: number
}>()

const emit = defineEmits<{
  (e: 'update:selectedEnvironment', value: string): void
  (e: 'update:selectedSeason', value: number): void
  (e: 'update:selectedPlayers', value: number): void
  (e: 'update:page', value: number): void
}>()

const page = computed({
  get: () => props.page,
  set: value => emit('update:page', value),
})

function formatDuration(duration: number): string {
  const seconds = Math.floor(duration / 1000) % 60
  const minutes = Math.floor((duration / 1000) / 60)
  return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <slot />
    <div class="flex flex-col gap-2">
      <div
        class="relative z-10 mx-4 mt-4 laptop:mx-16 grid grid-cols-[3em_4fr_3em] mobile:grid-cols-[3em_4fr_3em_4em] gap-2"
      >
        <span>{{ t(`jc.component.dungeonRunLeaderboard.place`) }}</span>
        <span>{{ t(`jc.component.dungeonRunLeaderboard.players`) }}</span>
        <span>{{ t(`jc.component.dungeonRunLeaderboard.room`) }}</span>
        <span class="text-right hidden mobile:block">{{ t(`jc.component.dungeonRunLeaderboard.time`) }}</span>
      </div>
      <template
        v-for="entry in props.runs"
        :key="entry.place"
      >
        <div
          class="rounded-md overflow-hidden shadow-lg border border-gray-200 bg-white flex flex-col hover:bg-slate-100 min-h-[3.5em]"
        >
          <div
            class="relative z-10 mx-4 my-auto laptop:mx-16 grid grid-cols-[3em_4fr_3em] mobile:grid-cols-[3em_4fr_3em_4em] gap-2 items-center"
          >
            <span class="text-primary font-bold">{{ entry.place }}</span>
            <div
              class="text-slate-900 grid grid-cols-1 tablet:grid-cols-2 data-[users=4]:grid-cols-4 gap-4 my-2"
              :data-users="entry.players.length"
            >
              <template
                v-for="(userId, index) in entry.players"
                :key="index"
              >
                <DungeonRunLeaderboardUser :user-id="userId" />
              </template>
            </div>
            <span class="text-slate-900 text-right">{{ entry.finishedLevel }}</span>
            <span class="text-slate-900 text-right hidden mobile:block">{{ formatDuration(entry.duration) }}</span>
          </div>
        </div>
      </template>
      <template v-if="props.runs === null">
        <template
          v-for="index in Array(10).keys()"
          :key="index"
        >
          <div
            class="rounded-md overflow-hidden shadow-lg border border-gray-200 flex flex-col hover:bg-slate-100 bg-gray-100 animate-pulse min-h-[3.5em]"
          />
        </template>
      </template>
      <template v-else-if="props.runs.length === 0">
        <span class="text-center py-4">{{ t(`jc.component.dungeonRunLeaderboard.empty`) }}</span>
      </template>
    </div>

    <Pagination
      v-model:page="page"
      :n-page-numbers="3"
      :n-pages="props.maxPage"
    />
  </div>
</template>

<style scoped lang="postcss">

</style>
